import { getTheatricalRelease, TheatricalReleaseResult } from 'src/services/Theatrical'
import { slugs } from '@/constants/slugs'
import { BASIC_PAGE_SLUGS } from '@/constants/theatricalUtils'
import { ServerExperiments } from '@/experimentation'
import { runExperimentsOnServer } from '@/experimentation/server'
import { TicketsLayout } from '@/layouts/TicketsLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getGuildStatus } from '@/services/GuildUserService'
import { getTheatricalReleasePromos } from '@/services/Theatrical/contentfulQueries'
import {
  getTheatricalUserPreferredVenues,
  getUserGuildDiscounts,
  getValidPublicDiscountsForRelease,
} from '@/services/Theatrical/queries'
import { getGiveawaySchedule } from '@/services/Theatrical/queries'
import { getUserAgent } from '@/services/UserAgentService'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ShowtimesView } from '@/views/TicketCheckoutViews/ShowtimesView'
import { InitializedReservation, initReservation } from '@/views/TicketCheckoutViews/ShowtimesView/initReservation'
import { TheatricalPromotion } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { pickCurrentPromo } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'

interface TicketsScenePageProps {
  basicPageExperimentEnabled: boolean
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  reservation: InitializedReservation
  experiments: ServerExperiments
  promo: TheatricalPromotion | null
  preferredVenueId: string | null
}

export const TicketsScenePage: NextPageWithLayout<TicketsScenePageProps> = ({
  basicPageExperimentEnabled,
  theatricalSlug,
  theatricalRelease,
  reservation,
  experiments,
  promo,
  preferredVenueId,
}) => {
  return (
    <TicketsLayout
      shouldShowLanguageModal
      isDarkMode={true}
      shouldRemoveNavOnMobile
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
    >
      <ShowtimesView
        isSceneModal
        basicPageExperimentEnabled={basicPageExperimentEnabled}
        theatricalRelease={theatricalRelease}
        theatricalSlug={theatricalSlug}
        reservation={reservation}
        experiments={experiments}
        promo={promo}
        preferredVenueId={preferredVenueId}
      />
    </TicketsLayout>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<TicketsScenePageProps>(
  async ({ federationClient, locale, next404, params, query, anonymousId, userId, req, res }) => {
    const theatricalSlug = getItemOrFirstEntry(params.slug)
    if (!theatricalSlug) return next404()

    // Skip reservation creation for bots to prevent unnecessary reservations
    const userAgentHeader = req.headers['user-agent']
    const { isBot } = getUserAgent(userAgentHeader)

    const [promos, publicDiscounts, giveawaySchedule, guildStatus, guildCodes, preferredVenues] = await Promise.all([
      getTheatricalReleasePromos({ theatricalSlug }),
      getValidPublicDiscountsForRelease(federationClient, theatricalSlug),
      getGiveawaySchedule(federationClient, theatricalSlug),
      getGuildStatus(federationClient),
      getUserGuildDiscounts(federationClient, userId, theatricalSlug),
      getTheatricalUserPreferredVenues(federationClient, userId),
    ])
    const isPremiumGuildUser = !!guildStatus?.isGuildMember && !!guildStatus?.guildRoles?.hasFreeTickets
    const promo = await pickCurrentPromo({
      promos: promos ?? null,
      query,
      theatricalSlug,
      hasGuildTickets: isPremiumGuildUser ? !!guildCodes?.length : false,
      publicDiscounts,
      giveawaySchedule,
      req,
      res,
    })

    const [reservation, theatricalRelease, translations, experiments] = await Promise.all([
      isBot ? Promise.resolve(null) : initReservation({ query, federationClient, theatricalSlug, anonymousId }),
      getTheatricalRelease({ theatricalSlug, client: federationClient }),
      loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account', 'theatrical-presales']),
      runExperimentsOnServer(req, res, [
        {
          experimentKey: 'david-sing-along-filter',
          defaultValue: false,
          skip: theatricalSlug !== slugs.david || promo?.id !== 'SINGALONG',
        },
        {
          experimentKey: 'solo-basic-showtime-page',
          defaultValue: false,
          skip: theatricalSlug !== slugs.soloMio,
        },
      ]),
    ])

    if (!theatricalRelease) return next404()

    const basicPageExperimentEnabled = Boolean(
      BASIC_PAGE_SLUGS.has(theatricalSlug) || experiments['solo-basic-showtime-page'],
    )

    return {
      props: {
        basicPageExperimentEnabled,
        theatricalSlug,
        theatricalRelease,
        reservation,
        experiments,
        promo,
        preferredVenueId: preferredVenues?.[0]?.providerId ?? null,
        ...translations,
      },
    }
  },
)

TicketsScenePage.getLayout = (page) => {
  return page
}
export default TicketsScenePage
